import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  Typography,
  Space,
  Button,
  Table,
  Tag,
  Input,
  message,
  Modal,
  Form,
  Alert,
  Row,
  Col,
  Select,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  ApiOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  EditOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { authAPI, externalAPI, default as apiService, getConfig } from '../services/api';
import { copyToClipboard } from '../utils/clipboard';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface ApiKey {
  id: number;
  api_key: string;
  name: string;
  is_active: boolean;
  created_at: string;
  category_id?: number;
  category_name?: string;
  category_is_default?: boolean;
}

interface Category {
  id: number;
  name: string;
  description?: string;
  is_default: boolean;
}

interface UsageRecord {
  id?: number;
  endpoint: string;
  method: string;
  status_code: number;
  response_time: number;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

const ApiDocs: React.FC = () => {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<number | null>(null);
  const [testForm] = Form.useForm();
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingApiKey, setEditingApiKey] = useState<ApiKey | null>(null);
  const [editForm] = Form.useForm();
  const [selectedApiKeyIds, setSelectedApiKeyIds] = useState<React.Key[]>([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usagePagination, setUsagePagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [priceMap, setPriceMap] = useState<Record<string, { price: number; is_free: boolean }>>({});
  const [apiBaseUrl, setApiBaseUrl] = useState(window.location.origin);

  useEffect(() => {
    getConfig().then(config => {
      if (config.apiUrl) {
        setApiBaseUrl(config.apiUrl.replace(/\/api$/, ''));
      }
    }).catch(() => {});
  }, []);
  const fetchPricing = useCallback(async () => {
    try {
      const res = await apiService.auth.getApiPricingForUser();
      if (res.data.status === 200 && res.data.data) {
        const map: Record<string, { price: number; is_free: boolean }> = {};
        (res.data.data as any[]).forEach((item: any) => {
          map[item.api_path] = { price: item.price, is_free: item.is_free };
        });
        setPriceMap(map);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const [expandedApis, setExpandedApis] = useState<Record<number, boolean>>({});

  const toggleApi = (index: number) => {
    setExpandedApis(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const [apiExamples, setApiExamples] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(true);

  useEffect(() => {
    const fetchDocs = async () => {
      try {
        const response = await authAPI.getApiDocs();
        if (response.data.status === 200 && response.data.docs) {
          setApiExamples(response.data.docs);
        }
      } catch (error) {
        console.error('获取API文档失败:', error);
      } finally {
        setDocsLoading(false);
      }
    };
    fetchDocs();
  }, []);

  // 获取API密钥列表
  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await authAPI.getApiKeys();
      // 根据新的状态码规范，检查响应结构
      if (response.data.status === 200 && response.data.data) {
        setApiKeys(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedApiKey(response.data.data[0].id);
        }
      } else {
        setApiKeys([]);
        message.error(response.data.error || '获取API密钥失败');
      }
    } catch (error: any) {
      console.error('获取API密钥错误:', error);
      setApiKeys([]);
      message.error(error.response?.data?.error || '获取API密钥失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取分类列表
  const fetchCategories = async () => {
    try {
      const response = await authAPI.getCategories();
      if (response.data.status === 200 && response.data.data) {
        setCategories(response.data.data);
        // 设置默认分类
        const defaultCategory = response.data.data.find((cat: Category) => cat.is_default);
        if (defaultCategory) {
          setSelectedCategoryId(defaultCategory.id);
        }
      }
    } catch (error: any) {
      console.error('获取分类失败:', error);
    }
  };

  // 获取使用记录
  const fetchUsageRecords = useCallback(async (page?: number, pageSize?: number) => {
    if (!selectedApiKey) return;
    
    const selectedKey = apiKeys.find(key => key.id === selectedApiKey);
    if (!selectedKey || !selectedKey.api_key) {
      setUsageRecords([]);
      return;
    }
    
    const currentPage = page || usagePagination.page;
    const currentPageSize = pageSize || usagePagination.pageSize;

    setUsageLoading(true);
    try {
      const response = await externalAPI.getUsage(selectedKey.api_key, { page: currentPage, limit: currentPageSize });
      if (response.data.status === 200 && response.data.data) {
        setUsageRecords(response.data.data);
        if (response.data.pagination) {
          setUsagePagination({
            page: response.data.pagination.page,
            pageSize: response.data.pagination.limit,
            total: response.data.pagination.total,
          });
        }
      } else {
        setUsageRecords([]);
        message.error(response.data.error || '获取使用记录失败');
      }
    } catch (error: any) {
      console.error('获取使用记录失败:', error);
      setUsageRecords([]);
      message.error(error.response?.data?.error || '获取使用记录失败');
    } finally {
      setUsageLoading(false);
    }
  }, [selectedApiKey, apiKeys, usagePagination.page, usagePagination.pageSize]);

  // 创建API密钥
  const handleCreateApiKey = async (values: { name: string; category_id?: number }) => {
    try {
      const response = await authAPI.createApiKey({ 
        name: values.name,
        category_id: values.category_id || selectedCategoryId
      });
      
      // 根据新的状态码规范，检查响应结构
      if (response.data.status === 200) {
        message.success('API密钥创建成功');
        fetchApiKeys();
        setCreateModalVisible(false);
        setNewApiKeyName('');
        setSelectedCategoryId(undefined);
      } else {
        message.error(response.data.error || '创建API密钥失败');
      }
    } catch (error: any) {
      console.error('创建API密钥错误:', error);
      message.error(error.response?.data?.error || '创建API密钥失败');
    }
  };

  // 检查是否为初始化创建的默认密钥（只有完全等于"默认秘钥"的才是初始化创建的）
  const isInitialApiKey = (apiKey: ApiKey) => {
    return apiKey.name === '默认秘钥';
  };

  // 删除API密钥
  const handleDeleteApiKey = async (id: number) => {
    const apiKey = apiKeys.find(key => key.id === id);
    if (apiKey && isInitialApiKey(apiKey)) {
      message.warning('初始化密钥不能删除');
      return;
    }

    try {
      const response = await authAPI.deleteApiKey(id);
      // 根据新的状态码规范，检查响应结构
      if (response.data.status === 200) {
        message.success('API密钥删除成功');
        fetchApiKeys();
      } else {
        message.error(response.data.error || '删除API密钥失败');
      }
    } catch (error: any) {
      console.error('删除API密钥错误:', error);
      message.error(error.response?.data?.error || '删除API密钥失败');
    }
  };

  // 批量删除API密钥
  const handleBatchDeleteApiKeys = async () => {
    if (selectedApiKeyIds.length === 0) {
      message.warning('请选择要删除的API密钥');
      return;
    }

    // 检查是否包含初始化密钥
    const initialKeys = apiKeys.filter(key => 
      selectedApiKeyIds.includes(key.id) && isInitialApiKey(key)
    );
    
    const deletableKeys = apiKeys.filter(key => 
      selectedApiKeyIds.includes(key.id) && !isInitialApiKey(key)
    );

    if (initialKeys.length > 0 && deletableKeys.length === 0) {
      // 全部都是初始化密钥，不能删除
      const initialNames = initialKeys.map(key => key.name).join('、');
      message.warning(`以下初始化密钥不能删除：${initialNames}`);
      return;
    }

    // 如果有初始化密钥，提示用户
    if (initialKeys.length > 0) {
      const initialNames = initialKeys.map(key => key.name).join('、');
      const confirmed = await new Promise((resolve) => {
        Modal.confirm({
          title: '确认批量删除',
          content: `将跳过以下初始化密钥：${initialNames}，是否继续删除其他 ${deletableKeys.length} 个密钥？`,
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      
      if (!confirmed) {
        return;
      }
    }

    try {
      const response = await authAPI.batchDeleteApiKeys(selectedApiKeyIds as number[]);
      
      if (response.data.status === 200) {
        message.success(response.data.message || '批量删除完成');
        setSelectedApiKeyIds([]);
        fetchApiKeys();
      } else {
        message.error(response.data.error || '批量删除失败');
      }
    } catch (error: any) {
      console.error('批量删除API密钥错误:', error);
      message.error(error.response?.data?.error || '批量删除失败');
    }
  };

  // 打开编辑API密钥模态框
  const handleEditApiKey = (apiKey: ApiKey) => {
    setEditingApiKey(apiKey);
    editForm.setFieldsValue({
      name: apiKey.name,
      category_id: apiKey.category_id,
    });
    setEditModalVisible(true);
  };

  // 更新API密钥
  const handleUpdateApiKey = async (values: { name: string; category_id: number }) => {
    if (!editingApiKey) return;

    try {
      const response = await authAPI.updateApiKey(editingApiKey.id, values);
      if (response.data.status === 200) {
        message.success('API密钥更新成功');
        setEditModalVisible(false);
        setEditingApiKey(null);
        editForm.resetFields();
        fetchApiKeys();
      } else {
        message.error(response.data.error || 'API密钥更新失败');
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || 'API密钥更新失败');
    }
  };

  // 复制API密钥
  const copyApiKey = async (key: string) => {
    const ok = await copyToClipboard(key);
    if (ok) message.success('API密钥已复制到剪贴板');
    else message.error('复制失败，请手动复制');
  };

  // 执行API测试
  const executeTest = async (values: { apiKey: number; endpoint: string; method: string; params?: string; dataId?: string }) => {
    if (!values.apiKey) {
      message.error('请先选择一个API密钥');
      return;
    }

    setTestLoading(true);
    try {
      let response;
      let params: any = {};

      if (values.params && values.params.trim()) {
        try {
          params = JSON.parse(values.params);
        } catch (parseError) {
          console.error('参数解析失败:', parseError);
          message.error('参数格式错误，请输入有效的JSON格式');
          return;
        }
      }

      // 根据选择的API密钥ID找到对应的API密钥字符串
      const selectedKey = apiKeys.find(key => key.id === values.apiKey);
      if (!selectedKey || !selectedKey.api_key) {
        message.error('请先选择API密钥');
        return;
      }
      
      // 根据方法执行不同的API调用
      switch (values.method) {
        case 'GET':
          if (values.endpoint.includes('/update')) {
            // 更新接口使用GET方法
            if (values.endpoint.includes('/batch')) {
              // 批量更新接口
              const batchParams = {
                ids: params.ids || [1, 2, 3], // 默认测试ID
                status: params.status || 'used',
                notes: params.notes || '测试更新'
              };
              response = await externalAPI.batchUpdateData(batchParams, selectedKey.api_key);
            } else {
              // 单个更新接口
              if (!values.dataId) {
                message.error('请输入要更新的数据ID');
                setTestLoading(false);
                return;
              }
              const updateParams = {
                status: params.status || 'used',
                notes: params.notes || '测试更新',
                response_data: params.response_data
              };
              response = await externalAPI.updateData(parseInt(values.dataId), updateParams, selectedKey.api_key);
            }
          } else if (values.endpoint.includes('/online')) {
            // 上报设备在线状态
            response = await externalAPI.reportOnline(selectedKey.api_key);
          } else if (values.endpoint.includes('/cluster/node')) {
            // 获取集群节点
            const clusterParams: { type?: string; enable_forward?: string } = {};
            if (params.type) clusterParams.type = params.type;
            if (params.enable_forward !== undefined) clusterParams.enable_forward = String(params.enable_forward);
            response = await externalAPI.getClusterNode(selectedKey.api_key, clusterParams);
          } else if (values.endpoint.includes('/wxdata')) {
            // 提交微信授权数据
            const wxUrl = params.url || 'https://example.com/qrcode';
            response = await externalAPI.getWxData(selectedKey.api_key, wxUrl);
          } else if (values.endpoint.includes('/wxpending')) {
            // 获取待授权数据
            response = await externalAPI.getWxPending(selectedKey.api_key);
          } else if (values.endpoint.includes('/wxstatus')) {
            // 更新微信授权状态
            const wxId = params.ID || params.id || 1;
            const wxStatus = params.status || 'authorized';
            const wxNotes = params.notes;
            response = await externalAPI.getWxStatus(selectedKey.api_key, Number(wxId), wxStatus, wxNotes);
          } else {
            // 普通GET请求 - 获取数据
            const getParams = {
              api_key: selectedKey.api_key,
              limit: params.limit || 1,
              status: params.status || 'unused'
            };
            response = await externalAPI.getData(getParams);
          }
          break;
        default:
          message.error('不支持的请求方法');
          return;
      }

      setTestResult({
        status: response.status,
        data: response.data,
        headers: response.headers
      });
      message.success('API测试成功');
    } catch (error: any) {
      setTestResult({
        status: error.response?.status || 'Error',
        data: error.response?.data || { error: error.message },
        headers: error.response?.headers || {}
      });
      message.error('API测试失败');
    } finally {
      setTestLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedApiKey) {
      setUsagePagination(prev => ({ ...prev, page: 1 }));
      fetchUsageRecords(1);
    }
  }, [selectedApiKey]);

  const apiKeyColumns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '分类',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (text: string, record: ApiKey) => {
        // 如果是默认分类，显示"默认"标签
        if (record.category_is_default) {
          return (
            <Tag color="blue" icon={<ExclamationCircleOutlined />}>
              默认
            </Tag>
          );
        }
        // 非默认分类也显示为标签样式
        if (text) {
          return (
            <Tag color="green" icon={<FileTextOutlined />}>
              {text}
            </Tag>
          );
        }
        // 未分类显示
        return <span>未分类</span>;
      },
    },
    {
      title: '密钥',
      dataIndex: 'api_key',
      key: 'api_key',
      render: (text: string) => (
        <Space>
          <Text code style={{ maxWidth: 200, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {text || 'N/A'}
          </Text>
          {text && (
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyApiKey(text)}
            >
              复制
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '激活' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: ApiKey) => {
        const isInitial = isInitialApiKey(record);
        return (
          <Space>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditApiKey(record)}
            >
              编辑
            </Button>
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              disabled={isInitial}
              onClick={() => handleDeleteApiKey(record.id)}
              title={isInitial ? '初始化密钥不能删除' : '删除密钥'}
            >
              删除
            </Button>
          </Space>
        );
      },
    },
  ];

  const usageColumns = [
    {
      title: '使用时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => {
        try {
          return new Date(date).toLocaleString('zh-CN');
        } catch (error) {
          return date || 'N/A';
        }
      },
    },
    {
      title: '端点',
      dataIndex: 'endpoint',
      key: 'endpoint',
      ellipsis: true,
    },
    {
      title: '方法',
      dataIndex: 'method',
      key: 'method',
      render: (method: string) => {
        const color = method === 'GET' ? 'blue' : method === 'POST' ? 'green' : method === 'PUT' ? 'orange' : 'red';
        return <Tag color={color}>{method}</Tag>;
      },
    },
    {
      title: '状态码',
      dataIndex: 'status_code',
      key: 'status_code',
      render: (code: number) => {
        const color = code >= 200 && code < 300 ? 'green' : code >= 400 ? 'red' : 'orange';
        return <Tag color={color}>{code}</Tag>;
      },
    },
    {
      title: '响应时间',
      dataIndex: 'response_time',
      key: 'response_time',
      render: (time: number) => `${time}ms`,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      ellipsis: true,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <ApiOutlined /> API接口文档
      </Title>
      
      <Tabs 
        defaultActiveKey="docs"
        items={[
          {
            key: 'docs',
            label: '接口文档',
            children: (
              <Card>
                <div style={{
                  marginBottom: 16,
                  padding: '16px 20px',
                  background: 'rgba(255, 255, 255, 0.72)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  borderRadius: 12,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}>
                  <div style={{
                    fontSize: 24,
                    color: '#1677ff',
                    background: 'rgba(22, 119, 255, 0.08)',
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <ApiOutlined />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#333', marginBottom: 2 }}>API接口说明</div>
                    <div style={{ fontSize: 13, color: '#8c8c8c' }}>以下是可用的API接口，所有接口都需要API密钥进行认证。</div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {docsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>加载中...</div>
                  ) : (
                    apiExamples.map((api, index) => {
                    const isExpanded = !!expandedApis[index];
                    const methodColor: Record<string, string> = { GET: 'blue', POST: 'green', PUT: 'orange', DELETE: 'red' };
                    return (
                      <Card
                        key={index}
                        size="small"
                        hoverable
                        bodyStyle={{ padding: '16px 20px' }}
                      >
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                          onClick={() => toggleApi(index)}
                        >
                          <div style={{
                            fontSize: 20,
                            color: '#5080ff',
                            background: 'rgba(80, 128, 255, 0.08)',
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <ApiOutlined />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 600, fontSize: 14 }}>{api.title}</span>
                              <Tag color={methodColor[api.method] || 'default'} style={{ marginRight: 0 }}>{api.method}</Tag>
                              {priceMap[api.url] && (
                                priceMap[api.url].is_free
                                  ? <span style={{ color: '#52c41a', fontSize: 12, fontWeight: 500, flexShrink: 0 }}>免费</span>
                                  : <span style={{ color: '#1677ff', fontSize: 12, fontWeight: 500, flexShrink: 0 }}>{priceMap[api.url].price}积分/次</span>
                              )}
                            </div>
                            <Space size={6}>
                              <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#595959' }}>{api.url}</span>
                            </Space>
                          </div>
                        </div>
                        {isExpanded && (
                          <div style={{ marginTop: 12, padding: '12px 16px', background: '#f6f8fa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                            <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 12 }}>{api.description}</div>
                            {api.params && (
                              <div style={{ marginBottom: 16 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#333' }}>参数说明</div>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                  {api.params && Object.entries(api.params).map(([key, value]) => (
                                    <li key={key} style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>
                                      <Text code>{key}</Text>: {String(value)}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>📋 请求示例</span>
                              <Button type="link" size="small" onClick={async () => { const ok = await copyToClipboard(api.example.request); if (ok) message.success('已复制到剪贴板'); else message.error('复制失败'); }}>复制</Button>
                            </div>
                            <div style={{ background: '#555', borderRadius: 12, padding: '12px 16px', marginBottom: 16, color: '#e0e0e0', fontSize: 13, fontFamily: 'monospace', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                              {api.example.request}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>✅ 响应示例</span>
                              <Button type="link" size="small" onClick={async () => { const ok = await copyToClipboard(api.example.response); if (ok) message.success('已复制到剪贴板'); else message.error('复制失败'); }}>复制</Button>
                            </div>
                            <div style={{ background: '#555', borderRadius: 12, padding: '12px 16px', color: '#e0e0e0', fontSize: 13, fontFamily: 'monospace', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                              {api.example.response}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })
                  )}
                </div>
              </Card>
            )
          },
          {
            key: 'keys',
            label: 'API密钥管理',
            children: (
              <Card>
                <div style={{ marginBottom: 16 }}>
                  <Space>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setCreateModalVisible(true);
                        const defaultCategory = categories.find((cat: Category) => cat.is_default);
                        if (defaultCategory) {
                          setSelectedCategoryId(defaultCategory.id);
                        }
                      }}
                    >
                      创建API密钥
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={fetchApiKeys}
                      loading={loading}
                    >
                      刷新
                    </Button>
                    {selectedApiKeyIds.length > 0 && (
                      <Button
                        danger
                        icon={<DeleteOutlined />}
                        onClick={handleBatchDeleteApiKeys}
                      >
                        批量删除 ({selectedApiKeyIds.length})
                      </Button>
                    )}
                  </Space>
                </div>
                
                <Table
                  columns={apiKeyColumns}
                  dataSource={apiKeys}
                  rowKey="id"
                  loading={loading}
                  pagination={false}
                  rowSelection={{
                    selectedRowKeys: selectedApiKeyIds,
                    onChange: (selectedRowKeys) => {
                      setSelectedApiKeyIds(selectedRowKeys);
                    },
                    getCheckboxProps: (record: ApiKey) => ({
                      disabled: isInitialApiKey(record),
                      name: record.name,
                    }),
                  }}
                />
              </Card>
            )
          },
          {
            key: 'test',
            label: 'API测试',
            children: (
              <Card>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form
                      form={testForm}
                      layout="vertical"
                      onFinish={executeTest}
                    >
                      <Form.Item
                        name="apiKey"
                        label="选择API密钥"
                        rules={[{ required: true, message: '请选择API密钥' }]}
                      >
                        <Select 
                          placeholder="选择要使用的API密钥"
                          value={selectedApiKey}
                          onChange={setSelectedApiKey}
                        >
                          {apiKeys && apiKeys.map(key => (
                            <Option key={key.id} value={key.id}>
                              {key.name} ({key.api_key ? key.api_key.substring(0, 8) : 'N/A'}...)
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      
                      <Form.Item
                        name="endpoint"
                        label="接口地址"
                        rules={[{ required: true, message: '请选择接口地址' }]}
                      >
                        <Select placeholder="选择要测试的接口">
                          {apiExamples.map((api: any) => (
                            <Option key={api.url} value={api.url}>{api.title}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                      
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.endpoint !== currentValues.endpoint}
                      >
                        {({ getFieldValue }) => {
                          const endpoint = getFieldValue('endpoint');
                          return endpoint === '/api/external/data/update' ? (
                            <Form.Item
                              name="dataId"
                              label="数据ID"
                              rules={[{ required: true, message: '请输入要更新的数据ID' }]}
                            >
                              <Input 
                                placeholder="请输入数据ID" 
                                type="number"
                              />
                            </Form.Item>
                          ) : null;
                        }}
                      </Form.Item>
                      
                      <Form.Item
                        name="method"
                        label="请求方法"
                        rules={[{ required: true, message: '请选择请求方法' }]}
                      >
                        <Select placeholder="选择请求方法">
                          <Option value="GET">GET</Option>
                          <Option value="PUT">PUT</Option>
                        </Select>
                      </Form.Item>
                      
                      <Form.Item
                        name="params"
                        label="请求参数 (JSON格式)"
                      >
                        <Input.TextArea 
                          rows={4} 
                          placeholder='例如: {"limit": 5, "status": "unused"}'
                        />
                      </Form.Item>
                      
                      <Form.Item>
                        <Button 
                          type="primary" 
                          htmlType="submit" 
                          loading={testLoading}
                        >
                          执行测试
                        </Button>
                        {(!apiKeys || apiKeys.length === 0) && (
                          <Text type="danger" style={{ marginLeft: 8 }}>
                            请先在API密钥管理页面创建密钥
                          </Text>
                        )}
                      </Form.Item>
                    </Form>
                  </Col>
                  
                  <Col span={12}>
                    {testResult && (
                      <div>
                        <Title level={5}>测试结果：</Title>
                        <div style={{ marginBottom: 8 }}>
                          <Text strong>状态码: </Text>
                          <Tag color={testResult.status < 400 ? 'green' : 'red'}>
                            {testResult.status}
                          </Tag>
                        </div>
                        <pre style={{ 
                          backgroundColor: '#f5f5f5', 
                          padding: '12px', 
                          borderRadius: '4px',
                          overflow: 'auto',
                          maxHeight: '300px'
                        }}>
                          {JSON.stringify(testResult.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </Col>
                </Row>
              </Card>
            )
          },
          {
            key: 'usage',
            label: '使用记录',
            children: (
              <Card>
                <div style={{ marginBottom: 16 }}>
                  <Space>
                    <Select
                      value={selectedApiKey}
                      onChange={setSelectedApiKey}
                      placeholder="选择API密钥查看使用记录"
                      style={{ width: 300 }}
                    >
                      {apiKeys && apiKeys.map(key => (
                        <Option key={key.id} value={key.id}>
                          {key.name} ({key.api_key ? key.api_key.substring(0, 8) : 'N/A'}...)
                        </Option>
                      ))}
                    </Select>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => fetchUsageRecords()}
                      loading={usageLoading}
                    >
                      刷新
                    </Button>
                  </Space>
                </div>
                
                <Table
                  columns={usageColumns}
                  dataSource={usageRecords}
                  rowKey={(record, index) => {
                    if (record.id) return record.id;
                    const baseKey = `${record.endpoint}-${record.created_at}-${record.status_code}-${record.ip_address}`;
                    const userAgentHash = record.user_agent ? btoa(record.user_agent).substring(0, 10) : 'unknown';
                    const responseTime = record.response_time || 0;
                    return `${baseKey}-${userAgentHash}-${responseTime}-${index}`;
                  }}
                  loading={usageLoading}
                  pagination={{ 
                    current: usagePagination.page,
                    pageSize: usagePagination.pageSize,
                    total: usagePagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                    pageSizeOptions: ['10', '20', '50', '100', '200', '300'],
                    onChange: (page, pageSize) => {
                      fetchUsageRecords(page, pageSize);
                    },
                  }}
                />
              </Card>
            )
          }
        ]}
      />
      
      {/* 创建API密钥Modal */}
      <Modal
        title="创建API密钥"
        open={createModalVisible}
        onOk={async () => {
          if (!newApiKeyName.trim()) {
            message.error('请输入密钥名称');
            return;
          }
          try {
            await handleCreateApiKey({ 
              name: newApiKeyName.trim(),
              category_id: selectedCategoryId
            });
          } catch (error) {
            // 错误已在handleCreateApiKey中处理
          }
        }}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewApiKeyName('');
          setSelectedCategoryId(undefined);
        }}
        okText="创建"
        cancelText="取消"
      >
        <div>
          <div style={{ marginBottom: 16 }}>
            <p>请输入API密钥名称：</p>
            <Input 
              placeholder="请输入密钥名称" 
              value={newApiKeyName}
              onChange={(e) => {
                setNewApiKeyName(e.target.value);
              }}
            />
          </div>
          <div>
            <p>选择分类：</p>
            <Select
              placeholder="选择分类"
              value={selectedCategoryId}
              onChange={setSelectedCategoryId}
              style={{ width: '100%' }}
            >
              {categories.map(category => (
                <Option key={category.id} value={category.id}>
                  {category.is_default ? (
                    <Tag color="blue" icon={<ExclamationCircleOutlined />}>
                      默认
                    </Tag>
                  ) : (
                    <Tag color="green" icon={<FileTextOutlined />}>
                      {category.name}
                    </Tag>
                  )}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>

      {/* 编辑API密钥Modal */}
      <Modal
        title="编辑API密钥"
        open={editModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingApiKey(null);
          editForm.resetFields();
        }}
        okText="更新"
        cancelText="取消"
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateApiKey}
        >
          <Form.Item
            name="name"
            label="密钥名称"
            rules={[{ required: true, message: '请输入密钥名称' }]}
          >
            <Input 
              placeholder="请输入密钥名称" 
              disabled={!!(editingApiKey && isInitialApiKey(editingApiKey))}
            />
          </Form.Item>
          {editingApiKey && isInitialApiKey(editingApiKey) && (
            <div style={{ marginTop: -16, marginBottom: 16, color: '#666', fontSize: '12px' }}>
              初始化密钥的名称不能修改
            </div>
          )}
          
          <Form.Item
            name="category_id"
            label="绑定分类"
            rules={[{ required: true, message: '请选择分类' }]}
          >
            <Select placeholder="选择分类">
              {categories.map((category: Category) => (
                <Option key={category.id} value={category.id}>
                  {category.is_default ? (
                    <Tag color="blue" icon={<ExclamationCircleOutlined />}>
                      默认
                    </Tag>
                  ) : (
                    <Tag color="green" icon={<FileTextOutlined />}>
                      {category.name}
                    </Tag>
                  )}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ApiDocs;